/**
 * English locale file.
 *
 * Mirrors the structure of zh-CN.js. Sync every entry when adding or modifying keys.
 */
export default {
  common: {
    // Buttons & Actions
    search: 'Search',
    reset: 'Reset',
    add: 'Add',
    edit: 'Edit',
    delete: 'Delete',
    confirm: 'Confirm',
    cancel: 'Cancel',
    ok: 'OK',
    submit: 'Submit',
    view: 'View',
    download: 'Download',
    moreBtn: 'More',
    enter: 'Enter',
    saveAndNext: 'Save & Next',

    // Table columns
    serialNo: '#',
    operation: 'Operation',

    // Form fields
    name: 'Name',
    gender: 'Gender',
    age: 'Age',
    birthDate: 'Birth Date',
    group: 'Group',
    item: 'Item',
    contactPhone: 'Phone',
    contactEmail: 'Email',
    certificateType: 'ID Type',
    certificateNumber: 'ID Number',
    headPhoto: 'Head Photo',
    certificateFrontPhoto: 'ID Front',
    certificateBackPhoto: 'ID Back',
    function: 'Role',
    project: 'Event',
    athlete: 'Athlete',
    registrationForm: 'Registration Form',
    voluntaryLiability: 'Voluntary Liability',

    // Gender enum
    male: 'Male',
    female: 'Female',

    // Certificate type enum (matches backend certificateType values)
    idCard: 'ID Card',
    drivingLicense: 'Driving License',
    passport: 'Passport',
    militaryCard: 'Military ID',
    hkMacauId: 'HK/Macau ID',
    taiwanPermit: 'Taiwan Permit',
    other: 'Other',

    // Project categories
    individualProject: 'Individual Events',
    pairTrainingProject: 'Pair Events',
    groupProject: 'Team Events',

    // Special labels
    trainingPartner: 'Training Partner',
    substitute: 'Substitute',
    comment: 'Note',
    position: 'Position',
    contact: 'Contact',
    contactInfo: 'Contact Info',

    // Statistics
    statistics: 'Statistics',
    totalPeople: 'Total',
    maleAthlete: 'Male Athletes',
    femaleAthlete: 'Female Athletes',

    // Prompts
    prompt: 'Notice',
    submittedLockTip: 'The registration has been submitted and cannot be modified. Please contact the administrator to withdraw it if needed.',

    // Placeholders
    pleaseInput: 'Please enter',
    pleaseInputKeyword: 'Please enter keyword',
    pleaseSelect: 'Please select',
    autoGenerate: 'Auto',

    // Unit suffixes
    people: '',
    upload: 'Upload',
  },

  // Form validation messages
  validate: {
    required: 'Please enter',
    requiredSelect: 'Please select',
    formatIncorrect: 'Invalid format',
    maxLength: 'Cannot exceed {max} characters',
    teamNameFormat: 'Team name cannot contain spaces or special characters',
  },

  // Network request errors
  request: {
    error: 'Request failed, please try again',
  },

  // === Page/component-specific entries ===

  header: {
    registerTime: 'Registration Time',
    logout: 'Logout',
  },

  footer: {
    browserTip: 'Recommended browsers: Edge, Chrome 80+, Firefox 86+, 360 Speed Mode',
    copyright: 'Copyright © 2025 Foshan One-Type Intelligent Technology Co., Ltd.',
    icp: '粤ICP备2024267651号-1',
    moreInfo: 'More info: www.one-type.com',
  },

  index: {
    systemName: 'One-Type Competition Registration System',
    registerTime: 'Registration Time',
    statusPending: 'Pending',
    statusOpen: 'Open',
    statusClosed: 'Closed',
    matchTime: 'Competition Time',
    matchVenue: 'Venue',
    warnNotStarted: 'Registration has not started yet',
    warnEnded: 'Registration has ended',
  },

  login: {
    registerTime: 'Registration Time',
    userLogin: 'User Login',
    fileDownload: 'File Download',
    downloadFailed: 'File download failed, please try again',
    specialNote: 'Special Notes',
    account: 'Account',
    password: 'Password',
    placeholderAccount: 'Please enter account',
    placeholderPassword: 'Please enter password',
    login: 'Login',
    backToIndex: 'Back to Home',
    goRegister: 'Register',
  },

  register: {
    registerTime: 'Registration Time',
    userRegister: 'User Registration',
    account: 'Account',
    password: 'Password',
    mobilePhone: 'Phone',
    placeholderAccount: 'Please enter account',
    placeholderPassword: 'Please enter password',
    placeholderMobile: 'Please enter phone number',
    registerAndLogin: 'Register & Login',
    goLogin: 'Login',
  },

  home: {
    baseRegistration: 'Registration',
    teamInfo: 'Team Info',
    athleteRegistration: 'Athlete Registration',
    teamOfficialMgmt: 'Team Officials',
    individualProject: 'Individual Events',
    pairTrainingProject: 'Pair Events',
    groupProject: 'Team Events',
    summaryAndPrint: 'Summary & Print',
    confirmLogout: 'Confirm logout?',
    configNotComplete: 'Please complete the competition form configuration first',
    needCompleteTeamInfoFirst: 'Please complete and save the team information first',
  },

  teamInfo: {
    title: 'Team Information',
    teamName: 'Team Name',
    contactName: 'Contact Name',
    contactPhone: 'Contact Phone',
    contactEmail: 'Contact Email',
    memberUnit: 'Member Unit',
    region: 'Region',
    placeholder: 'Please enter',
  },

  athleteInfo: {
    title: 'Athlete Information',
    addAthlete: 'Add Athlete',
    editAthlete: 'Edit Athlete',
    deleteConfirm: 'Are you sure to delete this athlete?',
    needCertNumber: 'Please enter ID number first',
    photoUploadFailed: 'Head photo upload failed',
    certFrontUploadFailed: 'ID front photo upload failed',
    certBackUploadFailed: 'ID back photo upload failed',
    customUploadFailed: 'Custom upload {0} failed',
    // API form field labels
    realNameCh: 'Name (CN)',
    realNameEn: 'Name (EN)',
    surnameCh: 'Surname (CN)',
    surnameEn: 'Surname (EN)',
    givenNameCh: 'Given Name (CN)',
    givenNameEn: 'Given Name (EN)',
  },

  personInfo: {
    title: 'Team Officials',
    addOfficial: 'Add Official',
    editOfficial: 'Edit Official',
    deleteConfirm: 'Are you sure to delete this official?',
    needCertNumber: 'Please enter ID number first',
    photoUploadFailed: 'Head photo upload failed',
    certFrontUploadFailed: 'ID front photo upload failed',
    certBackUploadFailed: 'ID back photo upload failed',
    customUploadFailed: 'Custom upload {0} failed',
    realNameCh: 'Name (CN)',
    realNameEn: 'Name (EN)',
    surnameCh: 'Surname (CN)',
    surnameEn: 'Surname (EN)',
    givenNameCh: 'Given Name (CN)',
    givenNameEn: 'Given Name (EN)',
  },

  personProject: {
    title: 'Individual Events',
    editProject: 'Edit Event',
    pleaseEnterComment: 'Please enter the project comment',
  },

  trainProject: {
    title: 'Pair Events',
    editProject: 'Edit Event',
    needSelectAthlete: 'Please select athlete',
    pleaseEnterComment: 'Please enter the comment',
  },

  teamProject: {
    title: 'Team Events',
    editProject: 'Edit Event',
    needSelectAthlete: 'Please select athlete',
    pleaseEnterComment: 'Please enter the project comment',
    selectPosition: 'Select position',
    pleaseSelectPosition: 'Please select a position for each checked athlete',
  },

  projectSum: {
    teamInfo: 'Team Info',
    title: 'Summary & Print',
    registrationForm: ' Registration Form',
    athleteInfo: 'Athlete Information',
    officialInfo: 'Team Officials',
    uploadStampedForm: 'Upload Stamped Form',
    uploadVoluntaryLiability: 'Upload Voluntary Liability',
    downloadForm: 'Download Form',
    onlyImageOrPdf: 'Only image or PDF files allowed!',
    needUploadForm: 'Please upload the registration form!',
    needUploadLiability: 'Please upload the voluntary liability form!',
    confirmSubmit: 'Confirm Submit Registration?',
  },

  manager: {
    teamInfo: 'Team Info',
    registerTime: 'Registration Time',
    logout: 'Logout',
    placeholderTeamName: 'Enter team name',
    placeholderContactPhone: 'Enter contact phone',
    placeholderStatus: 'Select status',
    teamName: 'Team Name',
    contactName: 'Contact Name',
    contactPhone: 'Contact Phone',
    contactEmail: 'Contact Email',
    memberUnitName: 'Member Unit',
    athleteCount: 'Athletes',
    submitStatus: 'Status',
    notSubmitted: 'Not Submitted',
    submitted: 'Submitted',
    submitTime: 'Submit Time',
    exportTeam: 'Export Teams',
    exportAthlete: 'Export Athletes',
    exportOfficial: 'Export Officials',
    submit: 'Submit',
    reject: 'Reject',
    teamDetail: 'Team Details',
    confirmSubmit: 'Confirm submit registration?',
    confirmReject: 'Confirm reject?',
    confirmLogout: 'Confirm logout?',
    athleteInfo: 'Athlete Information',
    officialInfo: 'Team Officials',
    downloadStampedForm: 'Stamped Form',
    downloadLiability: 'Voluntary Liability',
    viewAccount: 'View Account',
    account: 'Account',
    password: 'Password',
    delTeam: 'Delete Team',
    confirmDelTeam: 'Delete this team? Its athletes, event entries and team officials will also be deleted. This cannot be undone.',
  },
}
